import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Icons } from 'greener-ui-library';
import { BlueprintCalculations } from 'types';
import { currencyCodeToLocale, localizedNumber, localizedPrice } from 'utils/localization';
import { calculateSavingsAmount, calculateSavingsPercentage } from './utils';

export type TableRowData = {
  label: string;
  baseValue: string | JSX.Element;
  hybridValue: string | JSX.Element;
  savings?: string;
  savingsAmount?: string;
};

type TableSections = {
  costs: TableRowData[];
  environmental: TableRowData[];
  energySources: TableRowData[];
};

export const generateTableData = ({
  t,
  hasGrid = false,
  hasKite = false,
  baseCalculation,
  hybridCalculation,
  isEndDateProvided = false,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  hasGrid: boolean;
  hasKite: boolean;
  baseCalculation: BlueprintCalculations;
  hybridCalculation: BlueprintCalculations;
  isEndDateProvided?: boolean;
}): TableSections => {
  const costEstimatesLabel = isEndDateProvided
    ? t('blueprints:details.table.projectCostEstimates')
    : t('blueprints:details.table.weeklyCostEstimates');

  const ValueWithPercentage = ({ value, percentage }: { value?: number | null; percentage?: number | null }) => (
    <Box height={16}>
      <Typography variant="body2" lineHeight={0} mb={0.5}>
        {localizedNumber({
          value,
          unitLabel: 'kWh',
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        })}
      </Typography>
      <Typography variant="caption" color="grey.600">
        {localizedNumber({
          value: percentage,
          unitLabel: '%',
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 0,
        })}
      </Typography>
    </Box>
  );

  const isOnlyOneGensetUsedForHybrid =
    hybridCalculation.calculationResult.generatorUsageHours ===
    hybridCalculation.calculationResult.totalGeneratorUsageHours;

  return {
    costs: [
      {
        label: t('blueprints:details.table.rentalCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.rentalCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.rentalCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.rentalCostCents,
          hybridCalculation.calculationResult.rentalCostCents
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.rentalCostCents,
          hybridValue: hybridCalculation.calculationResult.rentalCostCents,
          formatter: (value) =>
            localizedPrice({
              value: value / 100,
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
            }),
        }),
      },
      {
        label: t('blueprints:details.table.additionalCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.additionalCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.additionalCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.additionalCostCents,
          hybridCalculation.calculationResult.additionalCostCents
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.additionalCostCents,
          hybridValue: hybridCalculation.calculationResult.additionalCostCents,
          formatter: (value) =>
            localizedPrice({
              value: value / 100,
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
            }),
        }),
      },
      ...(baseCalculation.calculationResult.co2CostCents
        ? [
            {
              label: t('blueprints:details.table.co2Costs'),
              baseValue: localizedPrice({
                value: (baseCalculation.calculationResult.co2CostCents ?? 0) / 100,
                locale: currencyCodeToLocale(baseCalculation.currencyCode),
              }),
              hybridValue: localizedPrice({
                value: (hybridCalculation.calculationResult.co2CostCents ?? 0) / 100,
                locale: currencyCodeToLocale(hybridCalculation.currencyCode),
              }),
              savings: calculateSavingsPercentage(
                baseCalculation.calculationResult.co2CostCents,
                hybridCalculation.calculationResult.co2CostCents
              ),
              savingsAmount: calculateSavingsAmount({
                baseValue: baseCalculation.calculationResult.co2CostCents,
                hybridValue: hybridCalculation.calculationResult.co2CostCents,
                formatter: (value) =>
                  localizedPrice({
                    value: value / 100,
                    locale: currencyCodeToLocale(hybridCalculation.currencyCode),
                  }),
              }),
            },
          ]
        : []),
      {
        label: t('blueprints:details.table.dieselCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.dieselCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.dieselCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.dieselCostCents,
          hybridCalculation.calculationResult.dieselCostCents
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.dieselCostCents,
          hybridValue: hybridCalculation.calculationResult.dieselCostCents,
          formatter: (value) =>
            localizedPrice({
              value: value / 100,
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
            }),
        }),
      },
      ...(hasGrid
        ? [
            {
              label: t('blueprints:details.table.electricityCosts'),
              baseValue: localizedPrice({
                value: baseCalculation.calculationResult.gridElectricityCostCents! / 100,
                locale: currencyCodeToLocale(baseCalculation.currencyCode),
              }),
              hybridValue: localizedPrice({
                value: hybridCalculation.calculationResult.gridElectricityCostCents! / 100,
                locale: currencyCodeToLocale(hybridCalculation.currencyCode),
              }),
              savings: calculateSavingsPercentage(
                baseCalculation.calculationResult.gridElectricityCostCents,
                hybridCalculation.calculationResult.gridElectricityCostCents
              ),
              savingsAmount: calculateSavingsAmount({
                baseValue: baseCalculation.calculationResult.gridElectricityCostCents,
                hybridValue: hybridCalculation.calculationResult.gridElectricityCostCents,
                formatter: (value) =>
                  localizedPrice({
                    value: value / 100,
                    locale: currencyCodeToLocale(hybridCalculation.currencyCode),
                  }),
              }),
            },
          ]
        : []),
      {
        label: costEstimatesLabel,
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.totalCostEstimateCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.totalCostEstimateCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.totalCostEstimateCents,
          hybridCalculation.calculationResult.totalCostEstimateCents
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.totalCostEstimateCents,
          hybridValue: hybridCalculation.calculationResult.totalCostEstimateCents,
          formatter: (value) =>
            localizedPrice({
              value: value / 100,
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
            }),
        }),
      },
    ],
    environmental: [
      {
        label: t('blueprints:details.table.totalGeneratorUsage'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.totalGeneratorUsageHours,
          unitLabel: t('blueprints:details.table.units.hours'),
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.totalGeneratorUsageHours,
          unitLabel: t('blueprints:details.table.units.hours'),
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.totalGeneratorUsageHours,
          hybridCalculation.calculationResult.totalGeneratorUsageHours
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.totalGeneratorUsageHours,
          hybridValue: hybridCalculation.calculationResult.totalGeneratorUsageHours,
          formatter: (value) =>
            localizedNumber({
              value,
              unitLabel: t('blueprints:details.table.units.hours'),
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
              fractionDigits: 1,
            }),
        }),
      },
      ...(isOnlyOneGensetUsedForHybrid
        ? []
        : [
            {
              label: t('blueprints:details.table.generatorUsage'),
              baseValue: localizedNumber({
                value: baseCalculation.calculationResult.generatorUsageHours,
                unitLabel: t('blueprints:details.table.units.hours'),
                locale: currencyCodeToLocale(baseCalculation.currencyCode),
                fractionDigits: 1,
              }),
              hybridValue: localizedNumber({
                value: hybridCalculation.calculationResult.generatorUsageHours,
                unitLabel: t('blueprints:details.table.units.hours'),
                locale: currencyCodeToLocale(hybridCalculation.currencyCode),
                fractionDigits: 1,
              }),
              savings: calculateSavingsPercentage(
                baseCalculation.calculationResult.generatorUsageHours,
                hybridCalculation.calculationResult.generatorUsageHours
              ),
              savingsAmount: calculateSavingsAmount({
                baseValue: baseCalculation.calculationResult.generatorUsageHours,
                hybridValue: hybridCalculation.calculationResult.generatorUsageHours,
                formatter: (value) =>
                  localizedNumber({
                    value,
                    unitLabel: t('blueprints:details.table.units.hours'),
                    locale: currencyCodeToLocale(hybridCalculation.currencyCode),
                    fractionDigits: 1,
                  }),
              }),
            },
          ]),
      {
        label: t('blueprints:details.table.fuelUsage'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.dieselUsageLiters,
          unitLabel: t('blueprints:details.table.units.liters'),
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.dieselUsageLiters,
          unitLabel: t('blueprints:details.table.units.liters'),
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.dieselUsageLiters,
          hybridCalculation.calculationResult.dieselUsageLiters
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.dieselUsageLiters,
          hybridValue: hybridCalculation.calculationResult.dieselUsageLiters,
          formatter: (value) =>
            localizedNumber({
              value,
              unitLabel: t('blueprints:details.table.units.liters'),
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
              fractionDigits: 1,
            }),
        }),
      },
      {
        label: t('blueprints:details.table.emissionsCo2'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.emissionsCo2Tons,
          unitLabel: t('blueprints:details.table.units.tonnes'),
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.emissionsCo2Tons,
          unitLabel: t('blueprints:details.table.units.tonnes'),
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.emissionsCo2Tons,
          hybridCalculation.calculationResult.emissionsCo2Tons
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.emissionsCo2Tons,
          hybridValue: hybridCalculation.calculationResult.emissionsCo2Tons,
          formatter: (value) =>
            localizedNumber({
              value,
              unitLabel: t('blueprints:details.table.units.tonnes'),
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
              fractionDigits: 1,
            }),
        }),
      },
      {
        label: t('blueprints:details.table.emissionsNox'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.emissionsNoxKg,
          unitLabel: 'kg',
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.emissionsNoxKg,
          unitLabel: 'kg',
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.emissionsNoxKg,
          hybridCalculation.calculationResult.emissionsNoxKg
        ),
        savingsAmount: calculateSavingsAmount({
          baseValue: baseCalculation.calculationResult.emissionsNoxKg,
          hybridValue: hybridCalculation.calculationResult.emissionsNoxKg,
          formatter: (value) =>
            localizedNumber({
              value,
              unitLabel: 'kg',
              locale: currencyCodeToLocale(hybridCalculation.currencyCode),
              fractionDigits: 1,
            }),
        }),
      },
      {
        label: t('blueprints:details.table.noisePollution'),
        baseValue: <Icons.Close color="error" />,
        hybridValue: <Icons.Check color="success" />,
      },
    ],
    energySources: [
      ...(hasGrid || hasKite
        ? [
            {
              label: t('blueprints:details.table.energySources.genset'),
              baseValue: (
                <ValueWithPercentage
                  value={baseCalculation.calculationResult.generatorUsageKwh}
                  percentage={baseCalculation.calculationResult.generatorUsagePercentage}
                />
              ),
              hybridValue: (
                <ValueWithPercentage
                  value={hybridCalculation.calculationResult.generatorUsageKwh}
                  percentage={hybridCalculation.calculationResult.generatorUsagePercentage}
                />
              ),
            },
          ]
        : []),
      ...(hasGrid
        ? [
            {
              label: t('blueprints:details.table.energySources.grid'),
              baseValue: (
                <ValueWithPercentage
                  value={baseCalculation.calculationResult.gridUsageKwh}
                  percentage={baseCalculation.calculationResult.gridUsagePercentage}
                />
              ),
              hybridValue: (
                <ValueWithPercentage
                  value={hybridCalculation.calculationResult.gridUsageKwh}
                  percentage={hybridCalculation.calculationResult.gridUsagePercentage}
                />
              ),
            },
          ]
        : []),
      ...(hasKite
        ? [
            {
              label: t('blueprints:details.table.energySources.wind'),
              baseValue: (
                <ValueWithPercentage
                  value={baseCalculation.calculationResult.kiteUsageKwh}
                  percentage={baseCalculation.calculationResult.kiteUsagePercentage}
                />
              ),
              hybridValue: (
                <ValueWithPercentage
                  value={hybridCalculation.calculationResult.kiteUsageKwh}
                  percentage={hybridCalculation.calculationResult.kiteUsagePercentage}
                />
              ),
            },
          ]
        : []),
    ],
  };
};
